import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "@/context/AuthContext";
import { useCrumbs } from "@/context/CrumbContext";
import NewAssessmentLauncher from "@/components/NewAssessmentLauncher";
import { ACTION_LABELS } from "@/pages/AuditLog";
import {
  Activity, AlertTriangle, Archive, ArrowUpRight, Camera, ClipboardCheck, FileCheck2, FileText, Loader2, MapPinned, Plus,
} from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

function QuickActions({ onNew, firstAssessment }) {
  const navigate = useNavigate();
  const items = [
    { id: "new", icon: Plus, title: "Yeni değerlendirme", text: "Bina seçip yönetmelik kontrolünü başlatın", onClick: onNew, primary: true },
    { id: "field", icon: Camera, title: "Saha modu", text: firstAssessment ? `Son: ${firstAssessment.title}` : "Önce değerlendirme oluşturun", onClick: () => firstAssessment && navigate(`/saha/${firstAssessment.id}`) },
    { id: "archive", icon: Archive, title: "Rapor arşivi", text: "Tüm raporlar ve revizyonlar", onClick: () => navigate("/reports") },
    { id: "map", icon: MapPinned, title: "Türkiye haritası", text: "Şehir bazlı risk görünümü", onClick: () => navigate("/map") },
  ];
  return (
    <section className="quick-actions" data-testid="home-quick-actions">
      {items.map(({ id, icon: Icon, title, text, onClick, primary }, i) => (
        <button key={id} className={`quick-card${primary ? " primary" : ""}`} onClick={onClick} style={{ animationDelay: `${i * 60}ms` }} data-testid={`quick-${id}`}>
          <Icon size={20}/><strong>{title}</strong><small>{text}</small><ArrowUpRight size={14} className="quick-arrow"/>
        </button>
      ))}
    </section>
  );
}

function Stats({ s }) {
  const items = [
    ["assessments", ClipboardCheck, s.assessments, "Değerlendirme", ""],
    ["open", AlertTriangle, s.open_findings, "Giderilecek madde", "red"],
    ["pending", Activity, s.pending, "Doğrulama bekleyen", "amber"],
    ["reports", FileText, s.reports_month, "Bu ay üretilen rapor", "blue"],
    ["signed", FileCheck2, s.signed_total, "İmzalı rapor", ""],
  ];
  return (
    <section className="metrics home-metrics" data-testid="home-stats">
      {items.map(([id, Icon, val, label, tone]) => (
        <div key={id} className="metric" data-testid={`home-stat-${id}`}>
          <div className={`metric-icon ${tone}`}><Icon size={18}/></div>
          <div><span>{label.toUpperCase()}</span><strong>{val}</strong></div>
        </div>
      ))}
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [showNew, setShowNew] = useState(false);
  useCrumbs([]);

  useEffect(() => { apiClient.get("/home").then((r) => setData(r.data)).catch(() => setData({ error: true })); }, []);

  if (!data) return <div className="page-loading"><Loader2 className="spin" size={20}/> Ana sayfa yükleniyor…</div>;
  if (data.error) return <div className="page-loading">Ana sayfa yüklenemedi. Sayfayı yenileyin.</div>;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";

  return (
    <div className="content-wrap" data-testid="home-page">
      <div className="page-heading">
        <div>
          <div className="eyebrow">HAZMATCORE · YANGIN UYGUNLUK MERKEZİ</div>
          <h1 data-testid="home-greeting">{greet}, {user?.name?.split(" ")[0] || ""}</h1>
          <p>Binalarınızın yangın yönetmeliğine uygunluğunu buradan takip edin.</p>
        </div>
      </div>
      <QuickActions onNew={() => setShowNew(true)} firstAssessment={data.recent_assessments[0]}/>
      <Stats s={data.stats}/>
      <section className="dashboard-grid">
        <div className="section-block">
          <div className="section-title">
            <div><div className="eyebrow">SON ÇALIŞMALAR</div><h2>Son değerlendirmeler</h2></div>
            <Link className="text-button" to="/assessments" data-testid="home-all-assessments">Tümünü gör <ArrowUpRight size={14}/></Link>
          </div>
          <div className="assessment-list" data-testid="home-recent-assessments">
            {data.recent_assessments.map((a) => (
              <button key={a.id} className="assessment-row" onClick={() => navigate(`/assessments/${a.id}`)} data-testid={`home-assessment-${a.id}`}>
                <div className="assessment-main">
                  <strong>{a.title}</strong>
                  <small>{a.building_name} · {a.location || "—"} · {a.counts.bad} uygunsuz · {a.counts.pending} bekleyen</small>
                </div>
                <span className="assessment-tag">{a.last_revision || a.status}</span>
                <ArrowUpRight size={14}/>
              </button>
            ))}
            {!data.recent_assessments.length && <div className="assessment-empty"><strong>Henüz değerlendirme yok</strong><small>"Yeni değerlendirme" ile başlayın.</small></div>}
          </div>
        </div>
        <div className="section-block">
          <div className="section-title">
            <div><div className="eyebrow">ARŞİV</div><h2>Son raporlar</h2></div>
            <Link className="text-button" to="/reports" data-testid="home-all-reports">Arşivi aç <ArrowUpRight size={14}/></Link>
          </div>
          <div className="assessment-list" data-testid="home-recent-reports">
            {data.recent_reports.map((r) => (
              <button key={r.id} className="assessment-row" onClick={() => navigate(`/assessments/${r.assessment_id}?tab=reports`)} data-testid={`home-report-${r.id}`}>
                <div className="assessment-main"><strong>{r.code} · {r.assessment_title}</strong><small>{fmt(r.created_at)} · {r.signed ? "İmzalı" : "İmzasız"}</small></div>
                <ArrowUpRight size={14}/>
              </button>
            ))}
            {!data.recent_reports.length && <div className="assessment-empty"><small>Henüz rapor yok.</small></div>}
          </div>
        </div>
      </section>
      <section className="section-block activity-block" data-testid="home-activity">
        <div className="section-title">
          <div><div className="eyebrow">KAYIT GEÇMİŞİ</div><h2>Son işlemler</h2></div>
          <Link className="text-button" to="/audit" data-testid="home-audit-link">Denetim günlüğü <ArrowUpRight size={14}/></Link>
        </div>
        {data.activity.map((a) => (
          <div className="activity-row" key={a.id}>
            <span className="activity-icon"><Activity size={15}/></span>
            <div><strong>{ACTION_LABELS[a.action] || a.action}</strong><small>{a.user_name}</small></div>
            <time>{fmt(a.created_at)}</time>
          </div>
        ))}
        {!data.activity.length && <small className="evidence-empty">Henüz işlem yok.</small>}
      </section>
      {showNew && <NewAssessmentLauncher onClose={() => setShowNew(false)}/>}
    </div>
  );
}
